import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ChevronRight, Image as ImageIcon } from "lucide-react";
import { ApiError } from "../../api/client";
import { catalogueApi, type ListingDetail } from "../../api/catalogue";
import { AddToBasket } from "../../components/cart/AddToBasket";
import { Breadcrumb, type Crumb } from "../../components/catalogue/Breadcrumb";
import { DiagramViewer } from "../../components/catalogue/DiagramViewer";
import { LoadError, SupersessionNotice } from "../../components/catalogue/Notices";
import { OriginBadge, PriceTag, ReferenceNumber, StockBadge } from "../../components/catalogue/partLabels";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Card, Icon, Table } from "../../components/ui";
import { formatPesos } from "../../lib/money";
import { describeQuantity } from "../../lib/quantity";
import { recentlyViewed } from "../../lib/recent";
import { useAddToBasket } from "../cart/useCart";
import { NotFound } from "./NotFoundRoute";
import { usePageTitle } from "./usePageTitle";

/** PRD screen 7: what the part is, its numbers, its price, and every machine it fits. */
export function ListingRoute() {
  const id = Number(useParams().id);
  const listing = useQuery({ queryKey: ["catalogue", "listing", id], queryFn: () => catalogueApi.listing(id) });
  usePageTitle(listing.data?.name ?? null);

  if (listing.isPending) return <PageWidth className="py-10 type-body text-text-muted">Loading…</PageWidth>;
  if (listing.isError) {
    if (listing.error instanceof ApiError && listing.error.status === 404) return <NotFound />;
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => listing.refetch()} />
      </PageWidth>
    );
  }
  return <ListingPage key={listing.data.id} listing={listing.data} />;
}

function ListingPage({ listing }: { listing: ListingDetail }) {
  const basket = useAddToBasket(listing.id);
  // For the home page's "Recently viewed".
  useEffect(() => recentlyViewed.add(listing.id), [listing.id]);
  const home = listing.fits[0];
  const crumbs: Crumb[] = home
    ? [
        { label: home.equipment_model.name, to: `/equipment-models/${home.equipment_model.id}` },
        { label: home.assembly.name, to: `/assemblies/${home.assembly.id}${home.position_number ? `?pos=${encodeURIComponent(home.position_number)}` : ""}` },
        { label: listing.name },
      ]
    : listing.product_type
      ? [{ label: "Supplies by type", to: "/product-types" }, { label: listing.product_type.name, to: `/product-types/${listing.product_type.id}` }, { label: listing.name }]
      : [{ label: "Catalogue", to: "/" }, { label: listing.name }];

  return (
    <PageWidth className="pt-6 pb-24 md:pb-0">
      <Breadcrumb items={crumbs} />

      <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:gap-12">
        <div className="flex min-w-0 flex-col gap-5">
          <Photos listing={listing} />
          {listing.drawn_on && (
            <div>
              <DiagramViewer
                image={listing.drawn_on.diagram}
                markers={listing.drawn_on.markers.map((marker, index) => ({
                  key: String(index),
                  position: 0,
                  label: listing.drawn_on!.position_number ?? "•",
                  x: marker.x_percent,
                  y: marker.y_percent,
                }))}
                selected={0}
                caption={listing.drawn_on.position_number ? `Where it fits · pos. ${listing.drawn_on.position_number}` : "Where it fits"}
                alt={`Diagram showing where ${listing.name} fits`}
              />
              <Link
                to={`/assemblies/${listing.drawn_on.assembly_id}${listing.drawn_on.position_number ? `?pos=${encodeURIComponent(listing.drawn_on.position_number)}` : ""}`}
                className="mt-2 inline-flex min-h-11 items-center gap-1 border-b-0 type-label"
              >
                Open the whole diagram <Icon icon={ChevronRight} size={16} />
              </Link>
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap gap-2">
            <StockBadge inStock={listing.in_stock} />
            <OriginBadge origin={listing.origin} />
          </div>
          <h1 className="m-0 type-h1 [font-size:var(--text-2xl)] tracking-tight">{listing.name}</h1>

          <Identity listing={listing} />

          {listing.replaced_by && (
            <div className="mt-5">
              <SupersessionNotice replacement={listing.replaced_by} inStock={listing.in_stock} />
            </div>
          )}

          <div className="my-6">
            <PriceTag cents={listing.price_cents} unit={listing.unit} size="lg" />
            <p className="mt-1 mb-0 type-small text-text-muted">Tax included. Delivery is quoted separately.</p>
          </div>

          <Card tone="sunken" padding="var(--space-6)">
            {/* On a phone the qty + Add control moves to the bar pinned above the tab nav (below); here it's inline (md and up). */}
            <div className="hidden flex-wrap items-center gap-4 md:flex">
              <AddToBasket listing={listing} onAdd={basket.onAdd} state={basket.state} />
              {!listing.in_stock && (
                <span className="type-small text-text-muted">{listing.replaced_by ? "Order the replacement above instead." : "We don't have any right now."}</span>
              )}
            </div>
            {listing.unit === "metre" && <p className="mt-4 mb-0 type-small text-text-muted">Sold by the metre, in whole metres.</p>}
            <p className="mt-4 mb-0 type-small text-text-muted">
              {listing.in_stock ? "Need more than we show, or by a certain day?" : "Want to know when it's back?"}{" "}
              <Link to={`/ask?listing=${listing.id}`}>Ask about this part</Link>
            </p>
          </Card>

          {listing.part_of_kits.map((kit) => (
            <Card key={kit.id} tone="green" padding="var(--space-5)" className="mt-5 border-green-300">
              <p className="mb-2 type-eyebrow tracking-[var(--tracking-widest)] text-green-800 uppercase">Also sold complete</p>
              <Link to={`/listings/${kit.id}`} className="type-h4 [font-size:var(--text-base)]">
                {kit.name}
              </Link>
              <p className="mt-1 mb-0 type-small text-text-body">
                This part is in the complete unit ({formatPesos(kit.price_cents)}). Buy the unit or this part, not both.
              </p>
            </Card>
          ))}

          {listing.kit_contents.length > 0 && (
            <section className="mt-6">
              <h2 className="mb-1 type-h4">What this unit contains</h2>
              <p className="mb-3 type-small text-text-muted">Each part is also sold on its own. The complete unit replaces all of them.</p>
              <Table
                rows={listing.kit_contents}
                rowKey={(row) => row.id}
                columns={[
                  { key: "number", label: "Part number", width: 150, render: (row) => <Link to={`/listings/${row.id}`} className="border-b-0"><ReferenceNumber listing={row} /></Link> },
                  { key: "name", label: "Description", render: (row) => <Link to={`/listings/${row.id}`}>{row.name}</Link> },
                  { key: "quantity", label: "Qty", width: 60, align: "center", mono: true },
                ]}
              />
            </section>
          )}
        </div>
      </div>

      <Fits listing={listing} />

      {/*
       * Phone only: pin the quantity + Add control above the bottom tab bar so the price's
       * primary action never scrolls away while the shopper reads the fitment table or specs
       * (mobile kit MobilePart's sticky bar; KALI-45). --tab-bar-height comes from StoreShell,
       * so the bar clears the tabs instead of overlapping them. Desktop keeps it inline above.
       */}
      <div className="fixed inset-x-0 bottom-[calc(var(--tab-bar-height)+env(safe-area-inset-bottom))] z-20 border-t border-border-subtle bg-surface-card px-4 py-3 shadow-[0_-4px_12px_rgba(36,35,31,.09)] md:hidden">
        <AddToBasket listing={listing} onAdd={basket.onAdd} state={basket.state} touch />
      </div>
    </PageWidth>
  );
}

/** Photos, or a clear note that there aren't any yet. */
function Photos({ listing }: { listing: ListingDetail }) {
  const [shown, setShown] = useState(0);
  const photo = listing.photos[shown];
  if (!photo) {
    return (
      <div className="flex aspect-[4/3] flex-col items-center justify-center gap-3 rounded-lg border border-border-subtle bg-[repeating-linear-gradient(45deg,var(--paper-0)_0_12px,#fff_12px_24px)] p-8 text-center">
        <Icon icon={ImageIcon} size={34} className="text-ink-300" />
        <span className="type-h4 [font-size:var(--text-base)] text-text-strong">Not photographed yet</span>
        <span className="max-w-[300px] type-small text-text-muted">We're working through the catalogue. The numbers here are checked against the manufacturer's book.</span>
      </div>
    );
  }
  return (
    <div>
      <a href={photo.full_url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg border border-border-subtle bg-white">
        <img src={photo.url} alt={`${listing.name}, photo ${shown + 1} of ${listing.photos.length}`} className="aspect-square w-full object-contain" />
      </a>
      {listing.photos.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {listing.photos.map((p, index) => (
            <button
              key={p.url}
              type="button"
              aria-label={`Show photo ${index + 1}`}
              aria-pressed={index === shown}
              onClick={() => setShown(index)}
              className={`size-16 cursor-pointer overflow-hidden rounded-sm border-2 bg-white ${index === shown ? "border-green-700" : "border-border-subtle"}`}
            >
              <img src={p.url} alt="" className="size-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NUMBER_LABEL = { manufacturer: "Manufacturer number", standard: "Made to standard", oem: "Cross-reference" } as const;

/** The numbers that identify the part, one row for each kind it carries. */
function Identity({ listing }: { listing: ListingDetail }) {
  const rows = [
    { label: "Kalikaya SKU", value: listing.sku },
    ...listing.part_numbers.map((pn) => ({
      label: NUMBER_LABEL[pn.kind],
      value: pn.number,
      note: pn.kind === "standard" ? "A standard shared with other parts. The size below tells them apart." : undefined,
    })),
    { label: "Size", value: listing.dimension ?? "—", note: listing.dimension ? undefined : "Not given in the manufacturer's book." },
  ];
  return (
    <dl className="mt-5 mb-0 rounded-lg border border-border-subtle bg-surface-card px-5">
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className="grid gap-1 border-b border-border-subtle py-3 last:border-b-0 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-5">
          <dt className="type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">{row.label}</dt>
          <dd className="m-0">
            <span className="type-sku [font-size:var(--text-base)] [overflow-wrap:anywhere] text-text-strong">{row.value}</span>
            {row.note && <span className="mt-0.5 block type-small text-text-muted">{row.note}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Every machine and diagram the part is on, grouped by machine (PRD 5.2). */
function Fits({ listing }: { listing: ListingDetail }) {
  if (listing.fits.length === 0) return null;
  const machines = [...new Map(listing.fits.map((fit) => [fit.equipment_model.id, fit.equipment_model])).values()];
  return (
    <section className="mt-12 max-w-[900px]">
      <h2 className="mb-1 type-h3">Fits these machines</h2>
      <p className="mb-5 type-small text-text-muted">
        {listing.fits.length === 1 ? "One position on one machine." : `${listing.fits.length} positions on ${machines.length} ${machines.length === 1 ? "machine" : "machines"}.`} The quantity is how many each position takes.
      </p>
      {machines.map((machine) => (
        <div key={machine.id} className="mb-6">
          <h3 className="mb-2 type-h4 [font-size:var(--text-base)]">
            <Link to={`/equipment-models/${machine.id}`}>{machine.name}</Link>
          </h3>
          <Table
            rows={listing.fits.filter((fit) => fit.equipment_model.id === machine.id)}
            rowKey={(fit, index) => `${fit.assembly.id}-${fit.position_number}-${index}`}
            columns={[
              {
                key: "assembly",
                label: "Diagram",
                render: (fit) => (
                  <Link to={`/assemblies/${fit.assembly.id}${fit.position_number ? `?pos=${encodeURIComponent(fit.position_number)}` : ""}`}>
                    {fit.assembly.name}
                  </Link>
                ),
              },
              { key: "code", label: "Code", width: 150, mono: true, render: (fit) => fit.assembly.code },
              { key: "pos", label: "Pos.", width: 70, align: "center", mono: true, render: (fit) => fit.position_number ?? "—" },
              { key: "qty", label: "Qty", width: 110, align: "center", mono: true, render: (fit) => describeQuantity(fit) },
            ]}
          />
        </div>
      ))}
    </section>
  );
}
